import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { ArrowRight, CalendarDays, Receipt, CheckCircle2, Clock, MapPin, Check, X, BellRing } from "lucide-react";
import { format, parseISO } from "date-fns";

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <div className="p-4 md:p-8 bg-white border border-[#EAE7E0]" data-testid={testId}>
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.3em] text-[#5C5652]">{label}</div>
        <Icon size={18} strokeWidth={1.5} className="text-[#C88270] shrink-0" />
      </div>
      <div className="font-serif text-3xl md:text-5xl tracking-tight text-[#2A2421]">{value}</div>
    </div>
  );
}

function eventTypeLabel(t) {
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}

function formatDate(d) {
  try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isAdmin = user?.role === "admin";
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);

  const load = async () => {
    try {
      const [s, e] = await Promise.all([api.get("/dashboard/summary"), api.get("/events")]);
      setSummary(s.data);
      setEvents(e.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcomingEvents = events.filter((ev) => ev.event_date >= today);

  // Team member: events where their assignment is still "invited"
  const myInvitations = !isAdmin
    ? upcomingEvents.filter((ev) => (ev.assigned || []).some((a) => a.team_member_id === user?.id && (a.status || "invited") === "invited"))
    : [];

  const respond = async (eventId, status) => {
    const current = (events.find((e) => e.id === eventId)?.assigned || []).find((a) => a.team_member_id === user?.id)?.status || "invited";
    if (current === status) return;
    const changing = current !== "invited";
    const currentLabel = current === "available" ? "Available" : "Not Available";
    const ok = await confirm({
      title: status === "available" ? "Confirm you're available?" : "Mark yourself not available?",
      description: changing
        ? `You currently have this marked as "${currentLabel}". ${status === "available" ? "Change it to Available — the studio will count you in for this event." : "Change it to Not Available — the studio may arrange a replacement."}`
        : (status === "available"
            ? "You'll be assigned to this event and the studio will plan around you. Continue?"
            : "The studio will be told you can't take this event. Continue?"),
      confirmText: status === "available" ? "Yes, I'm available" : "Yes, not available",
    });
    if (!ok) return;
    setResponding(`${eventId}-${status}`);
    try {
      await api.patch(`/events/${eventId}/respond`, { status });
      toast.success(status === "available" ? "Marked as available — you're assigned" : "Marked as not available");
      window.dispatchEvent(new Event("invitations-updated"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResponding(null);
    }
  };

  const displayEvents = (isAdmin ? upcomingEvents : upcomingEvents.filter((ev) => (ev.assigned || []).some((a) => a.team_member_id === user?.id && a.status === "available"))).slice(0, 6);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="dashboard-page">
      {/* Mobile elevated hero */}
      <div className="md:hidden -mx-6 -mt-10 mb-8 relative overflow-hidden rounded-b-[32px] border-b border-[#EAE7E0] bg-[#2A2421]" data-testid="dashboard-mobile-hero">
        <img src="/home-photography.webp" alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A2421] via-[#2A2421]/70 to-[#2A2421]/30" />
        <div className="relative z-10 px-6 pt-12 pb-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E4C9C0] mb-2">{isAdmin ? "Studio Overview" : "Your Overview"}</div>
          <h1 className="font-serif text-4xl tracking-tight text-[#FAF9F6] leading-tight">Hello, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-[13px] text-[#E8E2DA]/90 mt-3 max-w-sm">
            {isAdmin ? "Your weddings, at a glance — every event, trial and payout in one calm place." : "Your schedule and invitations, ready when you are."}
          </p>
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden md:block mb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">
          {isAdmin ? "Studio Overview" : "Your Overview"}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#2A2421]">
          Hello, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-[#5C5652] mt-4 max-w-xl">
          {isAdmin
            ? "A quiet moment before the season. Review upcoming events, trials and expenses across your studio."
            : "Here is what is scheduled for you, and the invitations awaiting your response."}
        </p>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            <Stat label="Upcoming" value={summary?.upcoming_events ?? 0} icon={CalendarDays} testId="stat-upcoming" />
            <Stat label="Past" value={summary?.past_events ?? 0} icon={Clock} testId="stat-past" />
            <Stat label="Pending Expenses" value={summary?.pending_expenses ?? 0} icon={Receipt} testId="stat-pending-expenses" />
            <Stat label="Approved Expenses" value={summary?.approved_expenses ?? 0} icon={CheckCircle2} testId="stat-approved-expenses" />
          </div>

          {/* Team member: pending invitations */}
          {!isAdmin && myInvitations.length > 0 && (
            <div className="mb-16" data-testid="pending-invitations">
              <div className="flex items-center gap-3 mb-6">
                <BellRing size={20} strokeWidth={1.5} className="text-[#C88270]" />
                <h2 className="font-serif text-3xl tracking-tight text-[#2A2421]">Pending Invitations</h2>
                <span className="text-xs uppercase tracking-[0.25em] text-[#C88270] bg-[#C88270]/10 px-3 py-1 rounded-full">{myInvitations.length}</span>
              </div>
              <div className="space-y-4">
                {myInvitations.map((ev) => (
                  <div key={ev.id} className="p-6 bg-white border border-[#C88270]/40 flex flex-col md:flex-row md:items-center gap-4" data-testid={`invitation-${ev.id}`}>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-1">{eventTypeLabel(ev.event_type)}</div>
                      <Link to={`/events/${ev.id}`} className="font-serif text-2xl tracking-tight text-[#2A2421] hover:text-[#C88270] transition-colors">{ev.couple_names || ev.title}</Link>
                      <div className="text-sm text-[#5C5652] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span className="flex items-center gap-1"><CalendarDays size={13} /> {formatDate(ev.event_date)} {ev.ready_by && `· ready by ${ev.ready_by}`}</span>
                        {ev.location && <span className="flex items-center gap-1"><MapPin size={13} /> {ev.location}</span>}
                      </div>
                      {ev.slots?.length > 0 && <div className="text-xs text-[#C88270] mt-1">Slots: {ev.slots.join(", ")}</div>}
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => respond(ev.id, "available")} disabled={responding} data-testid={`invitation-available-${ev.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2 bg-[#829384] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#6d7c6f] transition-colors disabled:opacity-50">
                        <Check size={14} /> Available
                      </button>
                      <button onClick={() => respond(ev.id, "unavailable")} disabled={responding} data-testid={`invitation-unavailable-${ev.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2 border border-red-700 text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors disabled:opacity-50">
                        <X size={14} /> Not Available
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin: reassignment alerts and understaffed events now live in dedicated tabs (/alerts, /staffing) */}

          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-serif text-3xl tracking-tight text-[#2A2421]">Upcoming</h2>
            <Link to="/events" className="text-xs uppercase tracking-[0.25em] text-[#5C5652] hover:text-[#C88270] flex items-center gap-2" data-testid="view-all-events">
              View all <ArrowRight size={14} />
            </Link>
          </div>

          {displayEvents.length === 0 ? (
            <div className="p-12 bg-white border border-[#EAE7E0] text-center">
              <div className="text-[#5C5652]">No upcoming events scheduled yet.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {displayEvents.map((ev) => (
                <Link
                  key={ev.id}
                  to={`/events/${ev.id}`}
                  data-testid={`event-card-${ev.id}`}
                  className="block p-4 md:p-6 bg-white border border-[#EAE7E0] rounded-2xl md:rounded-none hover:border-[#C88270]/60 transition-colors group"
                >
                  {/* Mobile: compact single-glance row */}
                  <div className="md:hidden flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#C88270]">{eventTypeLabel(ev.event_type)}</div>
                      <div className="font-serif text-lg leading-tight text-[#2A2421] truncate">{ev.couple_names || ev.title}</div>
                      {ev.location && <div className="text-[12px] text-[#5C5652] truncate flex items-center gap-1 mt-0.5"><MapPin size={12} strokeWidth={1.5} /> {ev.location}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-medium text-[#2A2421] whitespace-nowrap">{formatDate(ev.event_date)}</div>
                      {ev.ready_by && <div className="text-[11px] text-[#5C5652]">{ev.ready_by}</div>}
                    </div>
                  </div>

                  {/* Desktop: original card */}
                  <div className="hidden md:block">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-3">
                      {eventTypeLabel(ev.event_type)}
                    </div>
                    <div className="font-serif text-2xl tracking-tight text-[#2A2421] mb-1 group-hover:text-[#C88270] transition-colors">
                      {ev.couple_names || ev.title}
                    </div>
                    <div className="text-sm text-[#5C5652] mb-6">{ev.title}</div>
                    <div className="text-xs text-[#5C5652] flex items-center justify-between border-t border-[#EAE7E0] pt-4">
                      <span>{formatDate(ev.event_date)}</span>
                      <span>{ev.ready_by || ""}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
